import { WilEventTaget, LogEvent } from "../index";
import {
  deepFindLog,
  getClickPositionParams,
  IScope,
  isHtmlOrSvg,
} from "../utils/index";

const MESSAGE_EVENT_TYPE = "heatmap_inner_message"; // 内部传递message事件
const DEFAULT_CLICK_NAME = "search_heatmap_click"; // 默认点击事件名, 可自定义
const DEFAULT_ERROR_TYPE = "heatmap_inner_error";
const DEFAULT_ERROR_CLICK_TYPE = "heatmap_inner_click_error";
const DEFAULT_VERSION = "-1";
const DEFAULT_OPTIONS = {
  version: DEFAULT_VERSION,
  event: {
    click_name: DEFAULT_CLICK_NAME,
  },
} as IHeatmapConfig;

const catchSyncErrors = <T extends (...args: any) => any>(
  fn: T,
  errType = DEFAULT_ERROR_TYPE
): ((...args: Parameters<T>) => ReturnType<T>) => {
  return (...args: any[]) => {
    try {
      return fn(...args);
    } catch (err) {
      const tags = {
        error_type: errType,
      };
      // 目前还没有传入debug选项, 此处还是暴露出去吧
      console.error(err);
      try {
        if (window.Slardar) {
          window.Slardar("Sentry", (Sentry) => {
            Sentry.withScope((scope: IScope) => {
              scope.setTags(tags);
              // scope.setExtras(info);
              Sentry.captureException(err);
            });
          });
        }
      } catch (innerErr) {
        console.error("Sentry Error!");
      }
    }
  };
};

export interface IHeatmapConfig {
  event?: {
    click_name?: string;
  };
  version?: string;
}

interface IMessageData {
  tgt: HTMLElement | SVGAElement;
  logPath: Array<Object>;
  info: { type: string };
}

export class Heatmap {
  constructor(
    sendLog: (log: LogEvent) => void,
    options = DEFAULT_OPTIONS,
    wil: WilEventTaget
  ) {
    this.options = this.adaptConfig(options);
    this.init(sendLog);
    this.wil = wil;
  }

  cusEvent = document.createDocumentFragment();

  options: IHeatmapConfig;

  wil: WilEventTaget;

  init = (sendLog: (log: LogEvent) => void) => {
    // 统一接收数据, 调用sendLog
    this.cusEvent.addEventListener(
      MESSAGE_EVENT_TYPE,
      catchSyncErrors((e) => {
        const { detail } = e as CustomEvent;
        this.onMessage(detail, sendLog, this.options);
      })
    );

    // 进行绑定事件
    this.onClick();
  };

  onClick = () => {
    const { event } = this.options;
    const eventName = (event && event.click_name) || DEFAULT_CLICK_NAME;

    window.document.addEventListener(
      "click",
      catchSyncErrors((e: MouseEvent) => {
        const tgt = e.target as Node;
        if (!isHtmlOrSvg(tgt)) {
          return;
        }
        const params = getClickPositionParams(e);
        // 添加所属区域 - 设置展现埋点区域时有效
        if (params) {
          const area = this.findShowArea(tgt);
          this.cusEvent.dispatchEvent(
            new CustomEvent(MESSAGE_EVENT_TYPE, {
              detail: {
                tgt,
                logPath: [
                  {
                    event: eventName,
                    ...params,
                    ...(area && { module_name: area }),
                  },
                ],
                info: {
                  type: "heatmap_click",
                },
              },
            })
          );
        }
      }, DEFAULT_ERROR_CLICK_TYPE),
      true
    );
  };

  /**
   * 查找所属展现的area
   */
  findShowArea = (target: Element) => {
    if (!this.wil) {
      return undefined;
    }
    const attributeName = this.wil.config["data-log-view"];
    const attributeExtraName = this.wil.config["data-log-extra"];
    const log = deepFindLog(target, attributeName, attributeExtraName);

    if (log && log.length > 0) {
      let l = log.length;
      for (let i = 0; i < l; i++) {
        const { module_name } = (log[i] || {}) as any;

        // 如果存在
        if (module_name) {
          return module_name;
        }
      }
    }

    return undefined;
  };

  /**
   * 绑定统一的参数
   * @param data
   * @param sendLog
   * @param options
   */
  onMessage = (
    data: IMessageData,
    sendLog: (log: LogEvent) => void,
    options: IHeatmapConfig
  ) => {
    const { origin, pathname, search } = window.location;
    const { version = DEFAULT_VERSION } = options;
    const { tgt, logPath, info } = data;

    const log = {
      page_origin: origin,
      page_version: version,
      page_pathname: pathname,
      page_search: search,
    };
    logPath.push(log);
    sendLog(new LogEvent(tgt, logPath, info));
  };

  adaptConfig = (options: IHeatmapConfig) => {
    return {
      ...DEFAULT_OPTIONS,
      ...options,
      event: {
        ...DEFAULT_OPTIONS.event,
        ...options.event,
      },
    };
  };
}
